import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowUpRight, Check, AlertTriangle, Info, Quote, Building2, MapPin, Mail, Phone, Clock, X, User } from "lucide-react";
import { API } from "../../App";
import {
  GOLD, GOLD_DARK, NAVY, Aurora, Reveal, SectionHead, Overline, CheckItem, SoonTag, Tag, Card, GoldLink, TextLink,
} from "./ui";
import {
  BUSINESS, SERVICE_AREAS, MODE_LABEL, PACKAGES, ONBOARDING, PORTAL_NOW, PORTAL_SOON, BOUNDARIES, PARTNERS,
} from "../../data/business/profile";
import { ARTICLES, ARTICLE_SLUGS } from "../../data/perspektiverArticles";
import { KNOWLEDGE, KNOWLEDGE_SLUGS, KNOWLEDGE_TRACKS, FEATURED_SLUGS } from "../../data/business/knowledge";
import { CalculatorsBlock } from "./Calculators";
import { API_TIMEOUT } from "../../utils/crawler";

const WRAP = "max-w-[1680px] mx-auto px-4 md:px-8 lg:px-12";
const PAD = "py-20 md:py-28";

const Section = ({ children, tone = "white", id, testid }) => {
  const bg = tone === "slate" ? "bg-slate-50" : tone === "dark" ? "" : "bg-white";
  return (
    <section id={id} data-testid={testid}
      className={`${PAD} ${bg} relative overflow-hidden`}
      style={tone === "dark" ? { backgroundColor: NAVY } : undefined}>
      {tone === "dark" && <Aurora grid />}
      <div className={`${WRAP} relative`}>{children}</div>
    </section>
  );
};

/* ---------------------------------------------------------------- blocks */

export const AreasBlock = ({ heading, intro, tone = "white" }) => {
  return (
    <Section tone={tone} testid="block-areas">
      <SectionHead overline="Syv områder" title={heading || "Hvad vi overtager"} intro={intro} className="mb-12" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0">
        {SERVICE_AREAS.map((a, i) => (
          <Reveal key={a.id} i={i % 2} className="group py-8 border-t border-slate-200">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-center gap-4">
                <span className="font-display text-sm font-semibold" style={{ color: GOLD_DARK }}>{a.n}</span>
                <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">{a.title}</h3>
              </div>
              <Tag>{MODE_LABEL[a.mode]}</Tag>
            </div>
            <p className="text-sm text-slate-500 italic mb-4">«{a.question}»</p>
            <p className="text-slate-700 leading-relaxed mb-5">{a.summary}</p>
            <ul className="space-y-2 mb-5">
              {a.items.map((it, k) => <CheckItem key={k}>{it}</CheckItem>)}
            </ul>
            {a.soonItems?.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <SoonTag />
                <span className="text-xs text-slate-500">{a.soonItems.join(" · ")}</span>
              </div>
            )}
            <TextLink to={a.to} testid={`area-link-${a.id}`}>
              Læs om {a.title.toLowerCase()}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </TextLink>
          </Reveal>
        ))}
      </div>
    </Section>
  );
};

export const PackagesBlock = ({ heading, intro, tone = "slate" }) => {
  return (
    <Section tone={tone} testid="block-packages">
      <SectionHead overline="Pakker"
        title={heading || "Du vælger, hvor meget du afleverer"}
        intro={intro || "Prisen fastsættes efter en driftsgennemgang, bilagsmængde, antal biler og antal medarbejdere. Vi offentliggør ikke priser, vi ikke kan stå ved."}
        className="mb-12" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {PACKAGES.map((p, i) => (
          <Reveal key={p.name} i={i}>
            <div className={`h-full flex flex-col rounded-2xl p-7 md:p-8 border transition-all duration-300 ${
              p.featured ? "bg-white shadow-2xl border-transparent" : "bg-white border-slate-200 hover:shadow-xl"
            }`} style={p.featured ? { boxShadow: "0 25px 60px -20px rgba(11,18,32,0.25)" } : undefined}
              data-testid={`package-${p.name.toLowerCase().replace(/\W/g, "-")}`}>
              {p.featured && (
                <div className="mb-4"><Tag>Mest valgt af vognmænd</Tag></div>
              )}
              <h3 className="font-display text-2xl font-medium text-slate-900">{p.name}</h3>
              <p className="text-sm mt-1 mb-6" style={{ color: GOLD_DARK }}>{p.tagline}</p>
              <ul className="space-y-2.5 flex-1">
                {p.items.map((it, k) => <CheckItem key={k}>{it}</CheckItem>)}
              </ul>
              <div className="mt-7 pt-5 border-t border-slate-200">
                <p className="text-xs uppercase tracking-[0.16em] text-slate-500 mb-1">Prismodel</p>
                <p className="text-sm text-slate-700">{p.unit}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-5 rounded-2xl p-6 md:p-8" style={{ backgroundColor: NAVY }}>
        <p className="text-white text-lg md:text-xl font-display font-medium text-center sm:text-left">
          Vi sender ikke et tilbud, før vi har set din forretning.
          <span className="text-slate-400 font-normal"> Driftsgennemgangen er gratis og uforpligtende.</span>
        </p>
        <GoldLink to="/business/kontakt#kontakt" testid="packages-cta">Få et tilbud</GoldLink>
      </Reveal>
    </Section>
  );
};

export const PortalBlock = ({ heading, intro, tone = "dark", full = false }) => {
  return (
    <Section tone={tone} testid="block-portal">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <SectionHead light overline="Votra Business Portal"
            title={heading || "Beviset på at arbejdet bliver gjort"}
            intro={intro || "Et softwarefirma giver dig et system. En bogholder giver dig et regnskab. Du får begge dele, og et menneske, der kender din forretning."} />
          <div className="mt-8 flex flex-wrap gap-3">
            {!full && (
              <GoldLink to="/business/portal" testid="portal-block-cta">Se portalen</GoldLink>
            )}
            <Link to="/mitvotra"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold border border-white/30 text-white hover:bg-white/10 transition-colors"
              data-testid="portal-login-cta">
              Log ind
            </Link>
          </div>
        </div>
        <div className="lg:col-span-7 space-y-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] mb-4 flex items-center gap-2" style={{ color: GOLD }}>
              <Check className="h-4 w-4" strokeWidth={3} /> Findes i dag
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {(full ? PORTAL_NOW : PORTAL_NOW.slice(0, 8)).map((f, i) => (
                <Reveal key={f.title} i={i % 4} className="border-t border-white/10 pt-4">
                  <p className="text-white font-medium text-[15px]">{f.title}</p>
                  <p className="text-slate-400 text-sm leading-relaxed mt-1">{f.desc}</p>
                </Reveal>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] mb-4 flex items-center gap-2 text-slate-400">
              <Clock className="h-4 w-4" /> Under udvikling, vises ikke som eksisterende
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {PORTAL_SOON.map((f, i) => (
                <Reveal key={f.title} i={i % 4} className="border-t border-dashed border-white/15 pt-4 opacity-80">
                  <p className="text-slate-200 font-medium text-[15px] flex items-center gap-2">{f.title}</p>
                  <p className="text-slate-400 text-sm leading-relaxed mt-1">{f.desc}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

export const BoundariesBlock = ({ heading, intro, tone = "white" }) => (
  <Section tone={tone} testid="block-boundaries">
    <SectionHead overline="Afgrænsning"
      title={heading || "Det gør vi ikke"}
      intro={intro || "Det stærkeste du kan vide om en leverandør, er hvor grænsen går. Her er vores."}
      className="mb-12" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {BOUNDARIES.map((b, i) => (
        <Reveal key={b.title} i={i % 3}>
          <div className="h-full rounded-2xl border border-slate-200 p-6 bg-slate-50/60" data-testid={`boundary-${i}`}>
            <div className="flex items-center gap-2.5 mb-3">
              <X className="h-4 w-4 text-slate-400" strokeWidth={3} />
              <h3 className="font-display text-lg font-medium text-slate-900">{b.title}</h3>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

export const OnboardingBlock = ({ heading, intro, tone = "dark" }) => (
  <Section tone={tone} testid="block-onboarding">
    <SectionHead light overline="Onboarding"
      title={heading || "Otte trin fra første samtale til månedlig drift"}
      intro={intro} className="mb-14" />
    <div className="relative">
      <div className="hidden md:block absolute left-[27px] top-2 bottom-2 w-px bg-white/15 overflow-hidden">
        <div className="votra-scanline" />
      </div>
      <div className="space-y-8 md:space-y-10">
        {ONBOARDING.map((s, i) => (
          <Reveal key={s.n} i={i} className="relative flex flex-col md:flex-row gap-4 md:gap-10" data-testid={`onboarding-${i}`}>
            <div className="flex-shrink-0">
              <div className="w-14 h-14 rounded-full flex items-center justify-center font-display text-base font-semibold relative z-10"
                style={{ backgroundColor: "rgba(212,175,55,0.12)", color: GOLD, border: `1px solid ${GOLD}` }}>
                {s.n}
              </div>
            </div>
            <div className="md:pt-2 md:border-b md:border-white/10 md:pb-8 md:flex-1">
              <h3 className="font-display text-xl md:text-2xl font-medium text-white mb-2">{s.title}</h3>
              <p className="text-slate-400 leading-relaxed max-w-2xl">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </Section>
);

export const PartnersBlock = ({ heading, intro, tone = "slate" }) => (
  <Section tone={tone} testid="block-partners">
    <SectionHead overline="Partnere" title={heading || "Partnernetværk med roller"} intro={intro} className="mb-12" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {PARTNERS.map((p, i) => (
        <Reveal key={p.name} i={i % 2}>
          <div className="h-full flex items-start gap-5 rounded-2xl bg-white border border-slate-200 p-6" data-testid={`partner-${i}`}>
            <div className="w-24 h-14 flex-shrink-0 flex items-center justify-center rounded-lg bg-slate-50 border border-slate-100 p-2">
              <img src={p.src} alt={p.name} loading="lazy" className="max-h-9 max-w-full object-contain" />
            </div>
            <div>
              <p className="font-semibold text-slate-900 text-[15px]">{p.name}</p>
              <p className="text-sm text-slate-600 leading-relaxed mt-1">{p.role}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

export const FactsBlock = ({ heading, tone = "white" }) => (
  <Section tone={tone} testid="block-facts">
    <SectionHead overline="Verificeret" title={heading || "Selskabsoplysninger"} className="mb-10" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[
        { icon: Building2, label: "Selskab", lines: [BUSINESS.legalName, `CVR ${BUSINESS.cvr}`] },
        { icon: MapPin, label: BUSINESS.office.label, lines: [BUSINESS.office.street, `${BUSINESS.office.zip} ${BUSINESS.office.city}`] },
        { icon: MapPin, label: BUSINESS.registered.label, lines: [BUSINESS.registered.street, `${BUSINESS.registered.zip} ${BUSINESS.registered.city}`] },
        { icon: Mail, label: "Kontakt", lines: [BUSINESS.email, BUSINESS.phone] },
      ].map((f, i) => (
        <Reveal key={f.label} i={i}>
          <Card testid={`fact-${i}`}>
            <f.icon className="h-5 w-5 mb-4" style={{ color: GOLD_DARK }} />
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">{f.label}</p>
            {f.lines.map((l) => <p key={l} className="text-slate-800 text-[15px] leading-relaxed">{l}</p>)}
          </Card>
        </Reveal>
      ))}
    </div>
    <p className="text-sm text-slate-500 mt-8 max-w-3xl leading-relaxed">{BUSINESS.groupLine}</p>
  </Section>
);

export const ArticlesBlock = ({ heading, intro, tone = "white", all = false }) => {
  const slugs = all ? KNOWLEDGE_SLUGS : FEATURED_SLUGS;
  return (
    <Section tone={tone} testid="block-articles">
      <SectionHead overline="Viden" title={heading || "Artikler"} intro={intro} className="mb-12" />
      {all && (
        <div className="flex flex-wrap gap-2 mb-10">
          {KNOWLEDGE_TRACKS.map((t) => (
            <span key={t} className="px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-[0.12em] border border-slate-200 text-slate-600">
              {t} · {slugs.filter((s) => KNOWLEDGE[s]?.track === t).length}
            </span>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {slugs.map((slug, i) => {
          const a = KNOWLEDGE[slug];
          if (!a) return null;
          return (
            <Reveal key={slug} i={i % 3}>
              <Link to={`/business/viden/${slug}`}
                className="group w-full h-full text-left bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-2xl hover:border-transparent transition-all duration-300 votra-glow-edge flex flex-col"
                data-testid={`article-card-${i}`}>
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={a.image} alt={a.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(11,18,32,0.35) 100%)" }} />
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] mb-3" style={{ color: GOLD_DARK }}>{a.track || a.tag}</p>
                  <h3 className="font-display text-xl font-medium text-slate-900 mb-3 leading-snug">{a.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-5 flex-1">{a.excerpt}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: GOLD_DARK }}>
                    Læs mere <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </Section>
  );
};

/** Rådgivere oprettet i admin (GET /api/business/advisors). Vises kun når der er mindst én. */
export const AdvisorsBlock = ({ heading, intro, tone = "white", fallback = null }) => {
  const [advisors, setAdvisors] = useState(null);

  useEffect(() => {
    axios.get(`${API}/business/advisors`, { timeout: API_TIMEOUT })
      .then((r) => setAdvisors(r.data || [])).catch(() => setAdvisors([]));
  }, []);

  if (advisors === null) return null;
  if (advisors.length === 0) return fallback;

  const photoSrc = (a) => {
    if (!a.photo_url) return null;
    return a.photo_url.startsWith("http") ? a.photo_url : `${API.replace(/\/api$/, "")}${a.photo_url}`;
  };

  return (
    <Section tone={tone} testid="block-advisors">
      <SectionHead overline="Rådgivere" title={heading || "Dem du kommer til at tale med"} intro={intro} className="mb-12" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {advisors.map((a, i) => (
          <Reveal key={a.id} i={i % 3}>
            <div className="h-full flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow duration-300"
              data-testid={`advisor-card-${i}`}>
              <div className="aspect-[4/5] bg-slate-100 relative overflow-hidden flex items-center justify-center">
                {photoSrc(a) ? (
                  <img src={photoSrc(a)} alt={a.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover object-top" />
                ) : (
                  <User className="h-12 w-12 text-slate-300" strokeWidth={1.2} />
                )}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-display text-xl font-medium text-slate-900">{a.name}</h3>
                {a.title && <p className="text-sm mt-1" style={{ color: GOLD_DARK }}>{a.title}</p>}
                {a.bio && <p className="text-sm text-slate-600 leading-relaxed mt-4">{a.bio}</p>}
                {a.areas?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {a.areas.map((area) => (
                      <span key={area} className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600">{area}</span>
                    ))}
                  </div>
                )}
                <div className="mt-5 pt-4 border-t border-slate-100 space-y-1.5 text-sm">
                  {a.email && (
                    <a href={`mailto:${a.email}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
                      <Mail className="h-3.5 w-3.5" style={{ color: GOLD_DARK }} /> {a.email}
                    </a>
                  )}
                  {a.phone && (
                    <a href={`tel:${a.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
                      <Phone className="h-3.5 w-3.5" style={{ color: GOLD_DARK }} /> {a.phone}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
};

/* ---------------------------------------------------------------- generiske */

const ProseBlock = ({ b, tone }) => (
  <Section tone={tone} testid="block-prose">
    <div className="max-w-3xl">
      {b.heading && <SectionHead title={b.heading} className="mb-6" />}
      <div className="space-y-5">
        {b.paragraphs.map((p, i) => (
          <Reveal as="p" i={i} key={i} className="text-slate-700 text-base md:text-lg leading-relaxed">{p}</Reveal>
        ))}
      </div>
    </div>
  </Section>
);

const CardsBlock = ({ b, tone }) => {
  const cols = b.items.length === 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-2 lg:grid-cols-3";
  return (
    <Section tone={tone} testid="block-cards">
      <SectionHead title={b.heading} intro={b.intro} className="mb-12" />
      <div className={`grid grid-cols-1 ${cols} gap-6`}>
        {b.items.map((it, i) => (
          <Reveal key={it.title} i={i % 4}>
            <Card className="h-full flex flex-col" testid={`card-${i}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <h3 className="font-display text-lg md:text-xl font-medium text-slate-900">{it.title}</h3>
                {it.badge === "soon" && <SoonTag />}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed flex-1">{it.desc}</p>
              {it.to && (
                <TextLink to={it.to} className="mt-5 self-start">
                  Læs mere <ArrowRight className="h-4 w-4" />
                </TextLink>
              )}
            </Card>
          </Reveal>
        ))}
      </div>
    </Section>
  );
};

const ChecklistBlock = ({ b, tone }) => (
  <Section tone={tone} testid="block-checklist">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-5">
        <SectionHead title={b.heading} intro={b.intro} />
      </div>
      <div className="lg:col-span-7">
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
          {b.items.map((it, i) => (
            <Reveal as="li" key={i} i={i % 4} className="flex items-start gap-3 border-t border-slate-200 pt-4">
              <Check className="h-4 w-4 mt-1 flex-shrink-0" style={{ color: GOLD_DARK }} strokeWidth={2.5} />
              <span className="text-slate-700 text-[15px] leading-relaxed">{it}</span>
            </Reveal>
          ))}
        </ul>
        {b.note && <p className="text-sm text-slate-500 mt-8 leading-relaxed">{b.note}</p>}
      </div>
    </div>
  </Section>
);

const SplitBlock = ({ b, tone }) => {
  const cols = b.columns.length >= 4 ? "md:grid-cols-2 lg:grid-cols-4" : b.columns.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  return (
    <Section tone={tone} testid="block-split">
      <SectionHead title={b.heading} intro={b.intro} className="mb-12" />
      <div className={`grid grid-cols-1 ${cols} gap-8 lg:gap-10`}>
        {b.columns.map((c, i) => (
          <Reveal key={c.title} i={i} className="border-t-2 pt-6" style={{ borderColor: i === 0 ? GOLD : "#e2e8f0" }}>
            <h3 className="font-display text-xl font-medium text-slate-900 mb-5">{c.title}</h3>
            <ul className="space-y-3">
              {c.items.map((it, k) => <CheckItem key={k}>{it}</CheckItem>)}
            </ul>
          </Reveal>
        ))}
      </div>
    </Section>
  );
};

const TableBlock = ({ b, tone = "slate" }) => (
  <Section tone={tone} testid="block-table">
    <SectionHead title={b.heading} intro={b.intro} className="mb-10" />
    <Reveal className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
      <table className="w-full text-left border-collapse min-w-[640px]">
        <thead>
          <tr className="border-b border-slate-200">
            {b.columns.map((c, i) => (
              <th key={i} className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {b.rows.map((row, i) => (
            <tr key={i} className={`border-b border-slate-100 last:border-0 ${i === b.rows.length - 1 ? "bg-amber-50/40" : ""}`}
                data-testid={`table-row-${i}`}>
              {row.map((cell, k) => (
                <td key={k} className={`px-5 py-4 align-top text-[15px] ${k === 0 ? "font-semibold text-slate-900" : "text-slate-600"}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Reveal>
    {b.note && <p className="text-sm text-slate-500 mt-6 max-w-3xl leading-relaxed">{b.note}</p>}
  </Section>
);

const StepsBlock = ({ b, tone = "dark" }) => (
  <Section tone={tone} testid="block-steps">
    <SectionHead light title={b.heading} intro={b.intro} className="mb-14" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {b.items.map((s, i) => (
        <Reveal key={i} i={i % 3} data-testid={`step-${i}`}>
          <div className="h-full border-t border-white/15 pt-6">
            <div className="w-12 h-12 rounded-full flex items-center justify-center font-display text-sm font-semibold mb-5"
              style={{ backgroundColor: "rgba(212,175,55,0.12)", color: GOLD, border: `1px solid ${GOLD}` }}>
              {s.n}
            </div>
            <h3 className="font-display text-xl font-medium text-white mb-2">{s.title}</h3>
            <p className="text-slate-400 leading-relaxed text-[15px]">{s.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

const CalloutBlock = ({ b, tone = "white" }) => {
  const warn = b.tone === "warn";
  const Icon = warn ? AlertTriangle : Info;
  return (
    <Section tone={tone} testid="block-callout">
      <Reveal className="max-w-4xl rounded-2xl border p-7 md:p-9"
        style={{ borderColor: warn ? "rgba(153,27,27,0.25)" : "rgba(212,175,55,0.4)", backgroundColor: warn ? "rgba(254,242,242,0.7)" : "rgba(212,175,55,0.06)" }}>
        <div className="flex items-start gap-4">
          <Icon className="h-5 w-5 mt-1 flex-shrink-0" style={{ color: warn ? "#991B1B" : GOLD_DARK }} />
          <div>
            <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900 mb-3">{b.title}</h3>
            <p className="text-slate-700 leading-relaxed">{b.text}</p>
            {b.link && (
              <TextLink to={b.link.to} className="mt-5" testid="callout-link">
                {b.link.label} <ArrowRight className="h-4 w-4" />
              </TextLink>
            )}
          </div>
        </div>
      </Reveal>
    </Section>
  );
};

/** Hentbart eksempeldokument. `b.file` er en sti på API'et, fx "/business/example-management-report". */
const FileBlock = ({ b, tone = "slate" }) => (
  <Section tone={tone} testid="block-file">
    <Reveal className="max-w-4xl rounded-2xl border border-slate-200 bg-white p-7 md:p-9 shadow-[0_18px_44px_-28px_rgba(11,18,32,0.35)]">
      <div className="flex flex-col md:flex-row md:items-center gap-6">
        <div className="flex-1">
          <Overline>{b.overline || "Eksempel"}</Overline>
          <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900 mb-3">{b.heading}</h3>
          <p className="text-slate-700 leading-relaxed">{b.text}</p>
          {b.note && <p className="text-xs text-slate-500 leading-relaxed mt-4">{b.note}</p>}
        </div>
        <a href={`${API}${b.file}`} target="_blank" rel="noopener noreferrer"
          className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold whitespace-nowrap transition-shadow hover:shadow-[0_0_40px_rgba(212,175,55,0.45)]"
          style={{ backgroundColor: GOLD, color: NAVY }} data-testid="file-block-download">
          {b.button || "Hent eksemplet"}
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
    </Reveal>
  </Section>
);

/** Kompakt oversigt over hvad kunden får adgang til. Ligger tidligt på forsiden, fordi
 *  Votra Business er platform plus rådgiver, ikke almindelig konsulentbistand. */
const PillarsBlock = ({ b, tone = "dark" }) => (
  <Section tone={tone} testid="block-pillars">
    <Reveal>
      <Overline light={tone === "dark"}>{b.overline || "Votra Business Portal"}</Overline>
      <h2 className={`font-display text-3xl md:text-[2.6rem] font-medium leading-[1.14] mb-5 ${tone === "dark" ? "text-white" : "text-slate-900"}`}>
        {b.heading}
      </h2>
      {b.intro && (
        <p className={`text-base md:text-lg max-w-3xl leading-relaxed mb-12 ${tone === "dark" ? "text-slate-300" : "text-slate-600"}`}>
          {b.intro}
        </p>
      )}
    </Reveal>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px rounded-2xl overflow-hidden border"
      style={{ borderColor: tone === "dark" ? "rgba(255,255,255,0.12)" : "#E2E8F0", backgroundColor: tone === "dark" ? "rgba(255,255,255,0.12)" : "#E2E8F0" }}>
      {b.items.map((it, i) => (
        <Reveal key={it.title} i={i}
          className={`p-7 md:p-8 ${tone === "dark" ? "bg-[#0B1220]" : "bg-white"}`}
          data-testid={`pillar-${i}`}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] mb-4" style={{ color: GOLD }}>{it.label}</p>
          <h3 className={`font-display text-xl font-medium mb-2.5 leading-snug ${tone === "dark" ? "text-white" : "text-slate-900"}`}>
            {it.title}
          </h3>
          <p className={`text-sm leading-relaxed ${tone === "dark" ? "text-slate-400" : "text-slate-600"}`}>{it.desc}</p>
        </Reveal>
      ))}
    </div>
    {b.link && (
      <Reveal className="mt-10">
        <GoldLink to={b.link.to} testid="pillars-cta">{b.link.label}</GoldLink>
      </Reveal>
    )}
  </Section>
);

/** Direkte svar på sidens hovedspørgsmål. H2'en er spørgsmålet, så AI-søgemaskiner og
 *  Googles uddrag kan tage svaret ud uden at skulle fortolke resten af siden. */
const AnswerBlock = ({ b, tone = "white" }) => (
  <Section tone={tone} testid="block-answer">
    <div className="max-w-3xl">
      <Reveal>
        <Overline>{b.overline || "Kort svar"}</Overline>
        <h2 className="font-display text-3xl md:text-4xl lg:text-[2.6rem] font-medium leading-[1.14] text-slate-900">
          {b.heading}
        </h2>
      </Reveal>
      <Reveal i={1} className="mt-8 rounded-2xl border-l-[3px] bg-slate-50 p-7 md:p-8"
        style={{ borderColor: GOLD }}>
        {b.paragraphs.map((p, i) => (
          <p key={i} className={`text-slate-800 text-base md:text-lg leading-relaxed ${i ? "mt-5" : ""}`}>{p}</p>
        ))}
      </Reveal>
      {b.facts?.length > 0 && (
        <Reveal i={2} className="mt-8">
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
            {b.facts.map((f, i) => <CheckItem key={i} testid={`answer-fact-${i}`}>{f}</CheckItem>)}
          </ul>
        </Reveal>
      )}
    </div>
  </Section>
);

/** Krydslinks til beslægtede sider, så ingen underside bliver en blind vej.
 *  Links er rigtige <a href> via <Link>, så de kan følges af en crawler. */
const RelatedBlock = ({ b, tone = "slate" }) => (
  <Section tone={tone} testid="block-related">
    <SectionHead overline="Relateret" title={b.heading || "Læs videre her"} intro={b.intro} className="mb-12" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {b.items.filter(Boolean).map((it, i) => (
        <Reveal key={it.to} i={i % 3}>
          <Link to={it.to} data-testid={`related-link-${i}`}
            className="group flex h-full flex-col bg-white border border-slate-200 rounded-2xl p-6 md:p-7 transition-all duration-300 hover:shadow-xl hover:border-transparent votra-glow-edge">
            <h3 className="font-display text-lg md:text-xl font-medium text-slate-900 mb-2.5 leading-snug">{it.title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed flex-1">{it.desc}</p>
            <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: GOLD_DARK }}>
              Læs mere
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </Link>
        </Reveal>
      ))}
    </div>
  </Section>
);

const EmptyBlock = ({ b, tone = "slate" }) => (
  <Section tone={tone} testid="block-empty">
    <Reveal className="max-w-3xl rounded-2xl border border-dashed border-slate-300 bg-white p-8 md:p-10 text-center mx-auto">
      <Quote className="h-8 w-8 mx-auto mb-5" style={{ color: GOLD }} strokeWidth={1.5} />
      <h3 className="font-display text-2xl font-medium text-slate-900 mb-3">{b.heading}</h3>
      <p className="text-slate-600 leading-relaxed">{b.text}</p>
    </Reveal>
  </Section>
);

export const FaqBlock = ({ items, heading, tone = "white" }) => {
  if (!items?.length) return null;
  return (
    <Section tone={tone} testid="block-faq">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-4">
          <SectionHead overline="Svar" title={heading || "Ofte stillede spørgsmål"} />
        </div>
        <div className="lg:col-span-8 divide-y divide-slate-200 border-t border-slate-200">
          {items.map((f, i) => (
            <Reveal key={i} i={i % 4} className="py-6" data-testid={`faq-${i}`}>
              <h3 className="text-lg font-semibold text-slate-900 mb-2.5 flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: GOLD }} />
                {f.q}
              </h3>
              <p className="text-slate-600 leading-relaxed pl-[18px]">{f.a}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  );
};

export const CtaBlock = ({ title, button, to = "/business/kontakt" }) => {
  return (
    <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundColor: NAVY }} data-testid="block-cta">
      <Aurora />
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: GOLD }} />
      <Reveal className={`${WRAP} relative text-center`}>
        <h2 className="font-display text-3xl md:text-[2.75rem] font-medium text-white mb-9 max-w-3xl mx-auto leading-[1.14]">{title}</h2>
        <GoldLink to={to} testid="cta-button">{button}</GoldLink>
      </Reveal>
    </section>
  );
};

/** Billedblok. Med `heading` bliver det et asymmetrisk opslag med tekst ved siden af,
 *  uden `heading` bliver det et bredt billedbånd med en kort billedtekst.
 *  `b.flip` lægger billedet til venstre. */
const MediaBlock = ({ b, tone = "white" }) => {
  const mork = tone === "dark";
  const ramme = (
    <figure className="relative m-0 h-full w-full overflow-hidden rounded-[20px]"
      style={{ boxShadow: "0 30px 70px -40px rgba(11,18,32,0.55)" }}>
      <img
        src={b.image}
        alt={b.alt || ""}
        loading="lazy"
        decoding="async"
        className="block h-full w-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-[1.03]"
      />
      <span aria-hidden className="pointer-events-none absolute inset-0 rounded-[20px]"
        style={{ boxShadow: "inset 0 0 0 1px rgba(212,175,55,0.35)" }} />
    </figure>
  );

  if (!b.heading) {
    return (
      <Section tone={tone} testid="block-media">
        <Reveal className="max-w-[1180px]">
          <div className="aspect-[21/9] overflow-hidden rounded-[20px]">{ramme}</div>
          {b.caption && (
            <figcaption className={`mt-5 max-w-2xl text-sm leading-relaxed ${mork ? "text-slate-300" : "text-slate-600"}`}>
              {b.caption}
            </figcaption>
          )}
        </Reveal>
      </Section>
    );
  }

  return (
    <Section tone={tone} testid="block-media">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <Reveal className={`lg:col-span-7 ${b.flip ? "lg:order-1" : "lg:order-2"}`}>
          <div className="aspect-[4/3] md:aspect-[16/10] overflow-hidden rounded-[20px]">{ramme}</div>
        </Reveal>
        <Reveal i={1} className={`lg:col-span-5 ${b.flip ? "lg:order-2" : "lg:order-1"}`}>
          {b.overline && <Overline light={mork}>{b.overline}</Overline>}
          <h3 className={`font-display text-2xl md:text-3xl lg:text-[2.4rem] font-medium leading-[1.12] mb-5 ${mork ? "text-white" : "text-slate-900"}`}>
            {b.heading}
          </h3>
          {b.text && <p className={`leading-relaxed ${mork ? "text-slate-300" : "text-slate-700"}`}>{b.text}</p>}
          {b.link && (
            <TextLink to={b.link.to} className="mt-6" testid="media-link">
              {b.link.label} <ArrowRight className="h-4 w-4" />
            </TextLink>
          )}
        </Reveal>
      </div>
    </Section>
  );
};

/* ---------------------------------------------------------------- renderer */

const TONES = ["white", "slate"];

export const Blocks = ({ blocks = [], onContact }) => (
  <>
    {blocks.map((b, i) => {
      const tone = TONES[i % 2];
      switch (b.type) {
        case "answer": return <AnswerBlock key={i} b={b} tone={tone} />;
        case "related": return <RelatedBlock key={i} b={b} tone={tone} />;
        case "prose": return <ProseBlock key={i} b={b} tone={tone} />;
        case "cards": return <CardsBlock key={i} b={b} tone={tone} />;
        case "checklist": return <ChecklistBlock key={i} b={b} tone={tone} />;
        case "split": return <SplitBlock key={i} b={b} tone={tone} />;
        case "table": return <TableBlock key={i} b={b} tone={tone} />;
        case "steps": return <StepsBlock key={i} b={b} />;
        case "callout": return <CalloutBlock key={i} b={b} tone={tone} />;
        case "media": return <MediaBlock key={i} b={b} tone={b.tone || tone} />;
        case "file": return <FileBlock key={i} b={b} tone={tone} />;
        case "pillars": return <PillarsBlock key={i} b={b} tone={b.tone || tone} />;
        case "empty": return <EmptyBlock key={i} b={b} tone={tone} />;
        case "areas": return <AreasBlock key={i} heading={b.heading} intro={b.intro} tone={tone} />;
        case "packages": return <PackagesBlock key={i} heading={b.heading} intro={b.intro} tone={tone} />;
        case "portal": return <PortalBlock key={i} heading={b.heading} intro={b.intro} full={b.full} />;
        case "onboarding": return <OnboardingBlock key={i} heading={b.heading} intro={b.intro} />;
        case "boundaries": return <BoundariesBlock key={i} heading={b.heading} intro={b.intro} tone={tone} />;
        case "partners": return <PartnersBlock key={i} heading={b.heading} intro={b.intro} tone={tone} />;
        case "facts": return <FactsBlock key={i} heading={b.heading} tone={tone} />;
        case "articles": return <ArticlesBlock key={i} heading={b.heading} intro={b.intro} tone={tone} all={b.all} />;
        case "advisors": return (
          <AdvisorsBlock key={i} heading={b.heading} intro={b.intro} tone={tone}
            fallback={b.fallback ? <EmptyBlock b={b.fallback} tone={tone} /> : null} />
        );
        case "calculators": return <CalculatorsBlock key={i} heading={b.heading} intro={b.intro} />;
        case "contact": return <div key={i}>{onContact}</div>;
        default: return null;
      }
    })}
  </>
);

export { Section, WRAP };
